/**
 * Fire Drill Log Tab Component
 * Manages fire drill documentation and fire detection device records
 */
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Flame, Printer, Trash2, Plus, Save, History, Edit, FileText, ShieldCheck, X, ToggleLeft, ToggleRight, RefreshCw, CheckCircle2, XCircle, AlertCircle, Upload, Download } from "lucide-react";
import { format } from "date-fns";
import { exportFireDrillPDF } from "@/utils/pdfExport";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const LOCATION_PRESETS = [
  "Kitchen", "Classroom A", "Classroom B", "Classroom C", "Classroom D",
  "Infant Room", "Toddler Room", "Pre-K Room", "School Age Room",
  "Gym / Play Room", "Cafeteria / Dining Room", "Office",
  "Hallway", "Bathroom", "Storage Room", "Outdoor Playground"
];

export const FireDrillLogTab = ({ centerName = '' }) => {
  const { organization } = useAuth();
  const effectiveCenterName = centerName || organization?.name || '';
  const [centerAddress, setCenterAddress] = useState('');
  const [centerPhone, setCenterPhone] = useState('');
  const [fireDrillPeriodStart, setFireDrillPeriodStart] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [fireDrillPeriodEnd, setFireDrillPeriodEnd] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [fireDrillEntries, setFireDrillEntries] = useState([]);
  const [complianceItem, setComplianceItem] = useState(null);
  const [showEditItem, setShowEditItem] = useState(false);
  const [editItemForm, setEditItemForm] = useState({ name: '', weight: 8, description: '' });
  const [savingItem, setSavingItem] = useState(false);
  const [savedFireDrills, setSavedFireDrills] = useState([]);
  const [professionalTestingDate, setProfessionalTestingDate] = useState("");
  const [devicePurchaseDate, setDevicePurchaseDate] = useState("");
  const [testingType, setTestingType] = useState("manual");
  const [savingFireDrill, setSavingFireDrill] = useState(false);
  const [showFireDrillHistory, setShowFireDrillHistory] = useState(false);
  const [allStaff, setAllStaff] = useState([]);
  const [editingDrillId, setEditingDrillId] = useState(null);
  const [editDrillValues, setEditDrillValues] = useState({});
  const [archiving, setArchiving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedDrills, setArchivedDrills] = useState([]);
  const [receiptMeta, setReceiptMeta] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [receiptDragOver, setReceiptDragOver] = useState(false);
  const receiptFileInputRef = useRef(null);
  const [monthlyInspections, setMonthlyInspections] = useState({
    month1: "", month2: "", month3: "", month4: "",
    month5: "", month6: "", month7: "", month8: "",
    month9: "", month10: "", month11: "", month12: ""
  });
  const [monthlyInitials, setMonthlyInitials] = useState({
    month1: "", month2: "", month3: "", month4: "",
    month5: "", month6: "", month7: "", month8: "",
    month9: "", month10: "", month11: "", month12: ""
  });

  useEffect(() => {
    fetchFireDrills();
    fetchFireDetectionSettings();
    fetchAllStaff();
    fetchComplianceItem();
    fetchFireDetectionReceipt();
  }, []);

  useEffect(() => {
    if (organization) {
      setCenterAddress(prev => prev || organization.address || '');
      setCenterPhone(prev => prev || organization.phone || '');
    }
  }, [organization]);

  const fetchFireDetectionReceipt = async () => {
    try {
      const res = await axios.get(`${API}/fire-detection-settings/receipt`);
      setReceiptMeta(res.data || null);
    } catch {}
  };

  const uploadFireDetectionReceipt = async (file) => {
    if (!file) return;
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a PDF, DOC, DOCX, or image file (JPG, PNG)");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }
    setUploadingReceipt(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/fire-detection-settings/receipt/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setReceiptMeta(res.data);
      toast.success("Receipt uploaded");
      if (receiptFileInputRef.current) receiptFileInputRef.current.value = "";
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload receipt");
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleReceiptFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFireDetectionReceipt(file);
  };

  const handleReceiptDrop = (e) => {
    e.preventDefault();
    setReceiptDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFireDetectionReceipt(file);
  };

  const handleReceiptDragOver = (e) => {
    e.preventDefault();
    setReceiptDragOver(true);
  };

  const handleReceiptDragLeave = (e) => {
    e.preventDefault();
    setReceiptDragOver(false);
  };

  const handleReceiptDownload = async () => {
    try {
      const res = await axios.get(`${API}/fire-detection-settings/receipt/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', receiptMeta?.fileName || 'receipt');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download receipt");
    }
  };

  const handleReceiptDelete = async () => {
    try {
      await axios.delete(`${API}/fire-detection-settings/receipt`);
      setReceiptMeta(null);
      toast.success("Receipt removed");
    } catch {
      toast.error("Failed to remove receipt");
    }
  };

  const fetchAllStaff = async () => {
    try {
      const res = await axios.get(`${API}/staff-records`);
      const staff = Array.isArray(res.data) ? res.data : (res.data?.staff || res.data?.records || []);
      setAllStaff(staff.map(s => s.name).filter(Boolean));
    } catch {}
  };

  const fetchComplianceItem = async () => {
    try {
      const res = await axios.get(`${API}/compliance/status`);
      const item = (res.data?.items || []).find(i =>
        i.name?.toLowerCase().includes('fire drill') || i.name?.toLowerCase().includes('fire detection')
      );
      if (item) { setComplianceItem(item); setEditItemForm({ name: item.name, weight: item.weight || 8, description: item.description || '' }); }
    } catch {}
  };

  const handleSaveItemEdit = async () => {
    if (!complianceItem) return;
    setSavingItem(true);
    try {
      await axios.put(`${API}/compliance/items/${complianceItem.id}`, editItemForm);
      toast.success("Compliance item updated");
      setShowEditItem(false);
      fetchComplianceItem();
    } catch { toast.error("Failed to update"); } finally { setSavingItem(false); }
  };

  const handleToggleItem = async () => {
    if (!complianceItem) return;
    try {
      if (complianceItem.isEnabled !== false) {
        await axios.delete(`${API}/compliance/items/${complianceItem.id}`);
        toast.success("Item temporarily disabled");
      } else {
        await axios.put(`${API}/compliance/items/${complianceItem.id}`, { isEnabled: true });
        toast.success("Item restored");
      }
      fetchComplianceItem();
    } catch { toast.error("Failed to update item"); }
  };

  const handleDeleteItem = async () => {
    if (!complianceItem) return;
    if (!window.confirm(`Permanently delete "${complianceItem.name}"?`)) return;
    try {
      await axios.delete(`${API}/compliance/items/${complianceItem.id}/permanent`);
      toast.success("Item deleted");
      setComplianceItem(null);
    } catch { toast.error("Failed to delete"); }
  };

  const fetchFireDrills = async () => {
    try {
      const res = await axios.get(`${API}/fire-drills`);
      setSavedFireDrills(res.data?.drills || []);
    } catch (error) {
      console.error("Error fetching fire drills:", error);
    }
  };

  const fetchFireDetectionSettings = async () => {
    try {
      const res = await axios.get(`${API}/fire-detection-settings`);
      if (res.data) {
        setTestingType(res.data.testingType || "manual");
        setProfessionalTestingDate(res.data.professionalTestingDate || "");
        setDevicePurchaseDate(res.data.devicePurchaseDate || "");
        setMonthlyInspections(res.data.monthlyInspections || {});
        setMonthlyInitials(res.data.monthlyInitials || {});
        setFireDrillPeriodStart(res.data.periodStart || format(new Date(), 'yyyy-MM-dd'));
        setFireDrillPeriodEnd(res.data.periodEnd || format(new Date(), 'yyyy-MM-dd'));
      }
    } catch (error) {
      console.error("Error fetching fire detection settings:", error);
    }
  };

  const addFireDrillEntry = () => {
    const newEntry = {
      id: Date.now(),
      date: format(new Date(), 'yyyy-MM-dd'),
      timeHour: "10",
      timeMinute: "00",
      timePeriod: "AM",
      hypotheticalLocation: "",
      evacuationMinutes: "",
      evacuationSeconds: "",
      numberOfChildren: "",
      participatingStaff: [...allStaff]  // pre-load all staff, deletable
    };
    setFireDrillEntries([...fireDrillEntries, newEntry]);
  };

  const removeStaffFromEntry = (entryId, staffName) => {
    setFireDrillEntries(fireDrillEntries.map(entry =>
      entry.id === entryId
        ? { ...entry, participatingStaff: entry.participatingStaff.filter(s => s !== staffName) }
        : entry
    ));
  };

  const updateFireDrillEntry = (id, field, value) => {
    setFireDrillEntries(fireDrillEntries.map(entry => 
      entry.id === id ? { ...entry, [field]: value } : entry
    ));
  };

  const removeFireDrillEntry = (id) => {
    setFireDrillEntries(fireDrillEntries.filter(entry => entry.id !== id));
  };

  const handleSaveFireDrillEntry = async (entry) => {
    setSavingFireDrill(true);
    try {
      const drillData = {
        date: entry.date,
        time: `${entry.timeHour}:${entry.timeMinute} ${entry.timePeriod}`,
        hypotheticalLocation: entry.hypotheticalLocation,
        evacuationMinutes: entry.evacuationMinutes,
        evacuationSeconds: entry.evacuationSeconds,
        numberOfChildren: entry.numberOfChildren,
        participatingStaff: Array.isArray(entry.participatingStaff)
          ? entry.participatingStaff.join(', ')
          : entry.participatingStaff
      };
      
      await axios.post(`${API}/fire-drills`, drillData);
      toast.success("Fire drill entry saved");
      await fetchFireDrills();
    } catch (error) {
      console.error("Error saving fire drill:", error);
      toast.error("Failed to save fire drill entry");
    } finally {
      setSavingFireDrill(false);
    }
  };

  const handleDeleteFireDrill = async (drillId) => {
    try {
      await axios.delete(`${API}/fire-drills/${drillId}`);
      toast.success("Fire drill deleted");
      await fetchFireDrills();
    } catch (error) {
      toast.error("Failed to delete fire drill");
    }
  };

  const startEditDrill = (drill) => {
    const timeParts = (drill.time || '10:00 AM').split(' ');
    const period = timeParts[1] || 'AM';
    const [hour, minute] = (timeParts[0] || '10:00').split(':');
    setEditingDrillId(drill.id);
    setEditDrillValues({
      date: drill.date || '',
      timeHour: hour || '10',
      timeMinute: minute || '00',
      timePeriod: period,
      hypotheticalLocation: drill.hypotheticalLocation || '',
      evacuationMinutes: drill.evacuationMinutes || '',
      evacuationSeconds: drill.evacuationSeconds || '',
      numberOfChildren: drill.numberOfChildren || '',
      participatingStaff: drill.participatingStaff || '',
    });
  };

  const handleSaveDrillEdit = async (drillId) => {
    try {
      await axios.put(`${API}/fire-drills/${drillId}`, {
        date: editDrillValues.date,
        time: `${editDrillValues.timeHour}:${editDrillValues.timeMinute} ${editDrillValues.timePeriod}`,
        hypotheticalLocation: editDrillValues.hypotheticalLocation,
        evacuationMinutes: editDrillValues.evacuationMinutes,
        evacuationSeconds: editDrillValues.evacuationSeconds,
        numberOfChildren: editDrillValues.numberOfChildren,
        participatingStaff: editDrillValues.participatingStaff,
      });
      toast.success("Fire drill updated");
      setEditingDrillId(null);
      await fetchFireDrills();
    } catch {
      toast.error("Failed to update fire drill");
    }
  };

  const handleArchiveYear = async () => {
    if (savedFireDrills.length === 0) { toast.error("No drills to archive"); return; }
    setArchiving(true);
    try {
      const year = new Date().getFullYear();
      await Promise.all(savedFireDrills.map(d =>
        axios.put(`${API}/fire-drills/${d.id}`, { ...d, archived: true, archiveYear: year })
      ));
      toast.success(`${year} fire drills archived — ready for new year`);
      await fetchFireDrills();
    } catch { toast.error("Archive failed"); }
    finally { setArchiving(false); }
  };

  const fetchArchivedDrills = async () => {
    try {
      const res = await axios.get(`${API}/fire-drills?archived=true`);
      setArchivedDrills(res.data?.drills || []);
    } catch {}
  };

  const updateMonthlyInspection = (month, value) => {    setMonthlyInspections({ ...monthlyInspections, [month]: value });
  };

  const handleGenerateDates = () => {
    if (!fireDrillPeriodStart) return;
    const start = new Date(fireDrillPeriodStart + 'T12:00:00');
    const generated = {};
    for (let i = 0; i < 12; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i * 30);
      generated[`month${i + 1}`] = format(d, 'yyyy-MM-dd');
    }
    setMonthlyInspections(generated);
  };

  const handleClearAllDates = () => {
    setMonthlyInspections({
      month1: "", month2: "", month3: "", month4: "",
      month5: "", month6: "", month7: "", month8: "",
      month9: "", month10: "", month11: "", month12: ""
    });
  };

  const handleSaveFireDetectionSettings = async () => {
    try {
      await axios.put(`${API}/fire-detection-settings`, {
        testingType,
        professionalTestingDate,
        devicePurchaseDate,
        monthlyInspections,
        monthlyInitials,
        periodStart: fireDrillPeriodStart,
        periodEnd: fireDrillPeriodEnd
      });
      toast.success("Fire detection settings saved");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    }
  };

  const handleClearFireDrillLog = () => {
    setFireDrillPeriodStart(format(new Date(), 'yyyy-MM-dd'));
    setFireDrillPeriodEnd(format(new Date(), 'yyyy-MM-dd'));
    setFireDrillEntries([]);
    setProfessionalTestingDate("");
    setDevicePurchaseDate("");
    setMonthlyInspections({
      month1: "", month2: "", month3: "", month4: "",
      month5: "", month6: "", month7: "", month8: "",
      month9: "", month10: "", month11: "", month12: ""
    });
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) { toast.error("Allow popups to print"); return; }
    // Build a clean print-ready HTML document with the fire drill table
    const drillRows = [...fireDrillEntries, ...savedFireDrills].map(entry => {
      const staff = Array.isArray(entry.participatingStaff)
        ? entry.participatingStaff.join(', ')
        : (entry.participatingStaff || entry.participatingStaff || '');
      const time = entry.time || `${entry.timeHour || ''}:${entry.timeMinute || ''} ${entry.timePeriod || ''}`;
      const evac = entry.evacuationMinutes !== undefined
        ? `${entry.evacuationMinutes || 0}m ${entry.evacuationSeconds || '00'}s`
        : '';
      return `<tr>
        <td>${entry.date || entry.drillDate || ''}</td>
        <td>${time}</td>
        <td>${entry.hypotheticalLocation || ''}</td>
        <td>${evac}</td>
        <td>${entry.numberOfChildren || ''}</td>
        <td>${staff}</td>
      </tr>`;
    }).join('');

    const monthNames = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
    const monthHeaders = monthNames.map(m => `<th>${m}</th>`).join('');
    const monthKeys = ['month1','month2','month3','month4','month5','month6','month7','month8','month9','month10','month11','month12'];
    const monthValues = monthKeys.map(k => `<td>${monthlyInspections[k] || ''}${monthlyInitials[k] ? `<br/><small style="color:#555">${monthlyInitials[k]}</small>` : ''}</td>`).join('');

    printWindow.document.write(`<!DOCTYPE html><html><head><title>Fire Drill & Detection Log</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 24px; font-size: 11px; color: #000; }
      h1 { text-align: center; font-size: 15px; margin: 4px 0; }
      h2 { font-size: 11px; color: #003874; margin: 14px 0 4px; text-transform: uppercase; }
      .header { color: #003874; font-weight: bold; font-size: 9px; margin-bottom: 2px; }
      .period { text-align: center; font-size: 10px; margin: 6px 0; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
      th { background: #c8dcf5; font-size: 9px; padding: 4px 5px; border: 1px solid #aaa; text-align: left; }
      td { font-size: 9px; padding: 4px 5px; border: 1px solid #ccc; vertical-align: top; }
      tr:nth-child(even) td { background: #f5faff; }
      .detection-label { background: #c8dcf5; font-weight: bold; font-size: 9px; padding: 4px 6px; }
      .footer { margin-top: 12px; font-size: 10px; }
      hr { border-color: #003874; margin: 6px 0; }
      @media print { body { padding: 8px; } }
    </style></head><body>
    <div class="header">FIRE DRILL AND FIRE DETECTION LOG</div>
    <h1>FIRE DRILL AND FIRE DETECTION LOG</h1>
    ${effectiveCenterName ? `<div style="text-align:center;font-size:10px;font-weight:bold;">${effectiveCenterName}</div>` : ''}
    ${(centerAddress || centerPhone) ? `<div style="text-align:center;font-size:9px;color:#333;">${[centerAddress, centerPhone].filter(Boolean).join(' | ')}</div>` : ''}
    <div class="period">Period Covering: ${fireDrillPeriodStart || 'N/A'} to ${fireDrillPeriodEnd || 'N/A'}</div>
    <hr/>
    <h2>Fire Drill (Once Every 60 Days)</h2>
    <table>
      <thead><tr>
        <th>DATE OF DRILL</th><th>START TIME</th><th>HYPOTHETICAL LOCATION</th>
        <th>EVACUATION TIME</th><th>NUMBER OF CHILDREN</th><th>PARTICIPATING FACILITY PERSONS</th>
      </tr></thead>
      <tbody>${drillRows || '<tr><td colspan="6" style="text-align:center;color:#999;">No entries</td></tr>'}</tbody>
    </table>
    <h2>Fire Detection Devices or Systems</h2>
    <table>
      <thead><tr class="detection-label"><th colspan="12">MANUAL FIRE DETECTION TESTING DATE (Every 30 Days)</th></tr>
      <tr>${monthHeaders}</tr></thead>
      <tbody><tr>${monthValues}</tr></tbody>
    </table>
    <div class="footer">
      <strong>FIRE SAFETY PROFESSIONAL TESTING DATE (Annually):</strong> ${professionalTestingDate || '___________________'}
      <span style="margin-left:20px;color:#666;">(Documentation must be attached)</span><br/>
      <strong>FIRE DETECTION DEVICE/SYSTEM PURCHASE DATE:</strong> ${devicePurchaseDate || '___________________'}
    </div>
    <script>window.onload = function(){ window.print(); }</script>
    </body></html>`);
    printWindow.document.close();
  };

  const handleExportPDF = async () => {
    try {
      // Format drill entries to match the expected format in pdfExport.js
      const formattedDrills = fireDrillEntries.map(entry => ({
        date: entry.date,
        time: `${entry.timeHour}:${entry.timeMinute} ${entry.timePeriod}`,
        hypotheticalLocation: entry.hypotheticalLocation,
        evacuationMinutes: entry.evacuationMinutes,
        evacuationSeconds: entry.evacuationSeconds,
        numberOfChildren: entry.numberOfChildren,
        participatingStaff: Array.isArray(entry.participatingStaff)
          ? entry.participatingStaff.join(', ')
          : (entry.participatingStaff || '')
      }));
      
      const settings = {
        periodStart: fireDrillPeriodStart,
        periodEnd: fireDrillPeriodEnd,
        monthlyInspections,
        professionalTestingDate,
        devicePurchaseDate
      };
      
      await exportFireDrillPDF(formattedDrills, settings, effectiveCenterName, centerAddress, centerPhone);
      toast.success("Fire drill log PDF downloaded");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF");
    }
  };

  return (
    <Card className="bg-white/90 backdrop-blur-sm border-2 border-blue-200">
      <CardHeader className="print:hidden bg-gradient-to-r from-blue-100 to-cyan-100">
        <CardTitle className="text-2xl text-blue-800 flex items-center gap-3">
          <Flame className="w-7 h-7" />
          Fire Drill &amp; Detection Log
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Director Instructional Banner — print:hidden so it never goes to inspector */}
        <div className="print:hidden mb-4 bg-blue-900 rounded-xl px-5 py-4 flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <div className="w-6 h-6 rounded-full bg-yellow-400 flex items-center justify-center">
              <span className="text-blue-900 font-black text-xs">i</span>
            </div>
          </div>
          <div>
            <p className="text-white font-bold text-sm mb-1">Fire Drill Compliance Guidelines</p>
            <ul className="text-blue-100 text-xs space-y-1">
              <li>• Fire drills must be conducted <strong className="text-yellow-300">every 60 days or less</strong> — no gap between drills may exceed 60 days.</li>
              <li>• You are <strong className="text-yellow-300">compliant</strong> as long as your most recent drill was within the last 60 days and no prior gap exceeded 60 days.</li>
              <li>• For <strong className="text-yellow-300">inspection readiness</strong>: ensure at least one drill is on file for the month prior to your scheduled inspection month.</li>
              <li>• <strong className="text-yellow-300">Example</strong>: Inspection in June → compliant if a drill is logged by end of May.</li>
            </ul>
          </div>
        </div>

        {/* Preloaded Template Notice — informational only */}
        <div className="print:hidden mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-testid="preloaded-template-notice">
          <FileText className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            A preloaded Fire Drill &amp; Detection Log template is available below for your convenience.
          </p>
        </div>

        {/* Facility Info shown on printed report — pre-filled from your profile, editable if missing */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 print:hidden">
          <div>
            <Label className="text-blue-800 font-semibold">Facility Address (shown on report)</Label>
            <Input
              value={centerAddress}
              onChange={(e) => setCenterAddress(e.target.value)}
              placeholder="Enter facility address"
              className="border-blue-200 mt-1"
              data-testid="fire-drill-center-address-input"
            />
          </div>
          <div>
            <Label className="text-blue-800 font-semibold">Facility Phone (shown on report)</Label>
            <Input
              value={centerPhone}
              onChange={(e) => setCenterPhone(e.target.value)}
              placeholder="Enter facility phone"
              className="border-blue-200 mt-1"
              data-testid="fire-drill-center-phone-input"
            />
          </div>
        </div>

        {/* Period Selection */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 print:hidden">
          <div>
            <Label className="text-blue-800 font-semibold">Period Start Date</Label>
            <Input
              type="date"
              value={fireDrillPeriodStart}
              onChange={(e) => setFireDrillPeriodStart(e.target.value)}
              className="border-blue-200 mt-1"
            />
          </div>
          <div>
            <Label className="text-blue-800 font-semibold">Period End Date</Label>
            <Input
              type="date"
              value={fireDrillPeriodEnd}
              onChange={(e) => setFireDrillPeriodEnd(e.target.value)}
              className="border-blue-200 mt-1"
            />
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={addFireDrillEntry} className="bg-blue-500 hover:bg-blue-600 text-white">
              <Plus className="w-4 h-4 mr-2" /> Add Entry
            </Button>
          </div>
        </div>

        {/* Fire Drill Report Preview */}
        <div className="border-2 border-blue-100 rounded-lg p-6 bg-white print:border-0 print:p-0">
          <div className="text-center mb-6 border-b-2 border-blue-200 pb-4 print:border-black">
            <h2 className="text-2xl font-bold text-blue-800 print:text-black">FIRE DRILL AND FIRE DETECTION LOG</h2>
            {effectiveCenterName && (
              <p className="text-sm font-semibold text-blue-800 print:text-black mt-1">{effectiveCenterName}</p>
            )}
            {(centerAddress || centerPhone) && (
              <p className="text-xs text-gray-500 print:text-black">{[centerAddress, centerPhone].filter(Boolean).join(' | ')}</p>
            )}
            <div className="mt-2 text-sm">
              <span className="font-semibold">Period:</span>{" "}
              <span className="text-blue-700 print:text-black">
                {fireDrillPeriodStart ? format(new Date(fireDrillPeriodStart + 'T12:00:00'), 'MMM d, yyyy') : 'Not Set'} - {" "}
                {fireDrillPeriodEnd ? format(new Date(fireDrillPeriodEnd + 'T12:00:00'), 'MMM d, yyyy') : 'Not Set'}
              </span>
            </div>
          </div>

          {/* Fire Drill Entries Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-blue-100 print:bg-gray-200">
                  <th className="border border-blue-300 p-2 text-left text-blue-800 print:text-black font-semibold text-xs">DATE OF DRILL</th>
                  <th className="border border-blue-300 p-2 text-left text-blue-800 print:text-black font-semibold text-xs" style={{minWidth:'180px'}}>START TIME</th>
                  <th className="border border-blue-300 p-2 text-left text-blue-800 print:text-black font-semibold text-xs" style={{minWidth:'160px'}}>HYPOTHETICAL LOCATION</th>
                  <th className="border border-blue-300 p-2 text-left text-blue-800 print:text-black font-semibold text-xs" style={{minWidth:'150px'}}>EVACUATION TIME</th>
                  <th className="border border-blue-300 p-2 text-left text-blue-800 print:text-black font-semibold text-xs">NUMBER OF CHILDREN</th>
                  <th className="border border-blue-300 p-2 text-left text-blue-800 print:text-black font-semibold text-xs">PARTICIPATING FACILITY PERSONS</th>
                  <th className="border border-blue-300 p-2 text-center text-blue-800 print:hidden font-semibold text-xs w-16">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {fireDrillEntries.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-gray-500 border border-blue-100">
                      No fire drill entries. Click "Add Entry" to begin logging.
                    </td>
                  </tr>
                ) : (
                  fireDrillEntries.map((entry, index) => (
                    <tr key={entry.id} className={index % 2 === 0 ? 'bg-blue-50' : 'bg-white'}>
                      <td className="border border-blue-200 p-1 align-top">
                        <Input
                          type="date"
                          value={entry.date}
                          onChange={(e) => updateFireDrillEntry(entry.id, 'date', e.target.value)}
                          className="border-blue-200 text-sm h-8 min-w-[120px]"
                        />
                      </td>
                      <td className="border border-blue-200 p-1 align-top" style={{minWidth:'180px'}}>
                        <div className="flex gap-1 items-center flex-nowrap">
                          <select
                            value={entry.timeHour}
                            onChange={(e) => updateFireDrillEntry(entry.id, 'timeHour', e.target.value)}
                            className="border border-blue-200 rounded px-1 py-1 text-sm w-12"
                          >
                            {Array.from({length: 12}, (_, i) => i + 1).map((h) => (
                              <option key={h} value={String(h)}>{h}</option>
                            ))}
                          </select>
                          <span className="text-xs font-bold">:</span>
                          <select
                            value={entry.timeMinute}
                            onChange={(e) => updateFireDrillEntry(entry.id, 'timeMinute', e.target.value)}
                            className="border border-blue-200 rounded px-1 py-1 text-sm w-14"
                          >
                            {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map((m) => (
                              <option key={m} value={m}>{m}</option>
                            ))}
                          </select>
                          <select
                            value={entry.timePeriod}
                            onChange={(e) => updateFireDrillEntry(entry.id, 'timePeriod', e.target.value)}
                            className="border border-blue-200 rounded px-1 py-1 text-sm w-14"
                          >
                            <option value="AM">AM</option>
                            <option value="PM">PM</option>
                          </select>
                        </div>
                      </td>
                      <td className="border border-blue-200 p-1 align-top" style={{minWidth:'160px'}}>
                        <>
                          <input
                            list={`locations-${entry.id}`}
                            value={entry.hypotheticalLocation}
                            onChange={(e) => updateFireDrillEntry(entry.id, 'hypotheticalLocation', e.target.value)}
                            placeholder="Select or type location..."
                            className="border border-blue-200 rounded px-2 py-1 text-sm h-8 w-full focus:outline-none focus:ring-1 focus:ring-blue-400"
                          />
                          <datalist id={`locations-${entry.id}`}>
                            {LOCATION_PRESETS.map(loc => <option key={loc} value={loc} />)}
                          </datalist>
                        </>
                      </td>
                      <td className="border border-blue-200 p-1 align-top" style={{minWidth:'150px'}}>
                        <div className="flex gap-1 items-center flex-nowrap">
                          <Input
                            type="number"
                            value={entry.evacuationMinutes}
                            onChange={(e) => updateFireDrillEntry(entry.id, 'evacuationMinutes', e.target.value)}
                            placeholder="Min"
                            className="border-blue-200 text-sm h-8 w-14"
                            min="0"
                          />
                          <span className="text-xs font-medium">min</span>
                          <Input
                            type="number"
                            value={entry.evacuationSeconds}
                            onChange={(e) => updateFireDrillEntry(entry.id, 'evacuationSeconds', e.target.value)}
                            placeholder="Sec"
                            className="border-blue-200 text-sm h-8 w-14"
                            min="0"
                            max="59"
                          />
                          <span className="text-xs font-medium">sec</span>
                        </div>
                      </td>
                      <td className="border border-blue-200 p-1 align-top">
                        <Input
                          type="number"
                          value={entry.numberOfChildren}
                          onChange={(e) => updateFireDrillEntry(entry.id, 'numberOfChildren', e.target.value)}
                          placeholder="#"
                          className="border-blue-200 text-sm h-8 w-16"
                          min="0"
                        />
                      </td>
                      <td className="border border-blue-200 p-1 align-top" style={{minWidth:'200px'}}>
                        <div className="flex flex-wrap gap-1 min-h-[32px]">
                          {(Array.isArray(entry.participatingStaff) ? entry.participatingStaff : (entry.participatingStaff ? entry.participatingStaff.split(',').map(s=>s.trim()).filter(Boolean) : [])).map((name) => (
                            <span key={name} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-medium border border-blue-200">
                              {name}
                              <button
                                onClick={() => removeStaffFromEntry(entry.id, name)}
                                className="text-blue-400 hover:text-red-500 transition-colors ml-0.5"
                                title={`Remove ${name}`}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                          {(Array.isArray(entry.participatingStaff) ? entry.participatingStaff : []).length === 0 && (
                            <span className="text-xs text-gray-400 italic">No staff — add entry to reload</span>
                          )}
                        </div>
                      </td>
                      <td className="border border-blue-200 p-1 align-top print:hidden">
                        <div className="flex flex-col gap-1">
                          <Button onClick={() => handleSaveFireDrillEntry(entry)} variant="ghost" size="sm" className="text-green-600 hover:bg-green-50 h-7 w-7 p-0" title="Save">
                            <Save className="w-3.5 h-3.5" />
                          </Button>
                          <Button onClick={() => removeFireDrillEntry(entry.id)} variant="ghost" size="sm" className="text-red-500 hover:bg-red-50 h-7 w-7 p-0">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ── Saved Fire Drills — always visible, matches paper form top section ── */}
          <div className="mt-6 mb-2 print:hidden">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-purple-800 flex items-center gap-2">
                <History className="w-4 h-4" />
                Saved Fire Drills ({savedFireDrills.length})
              </h3>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => { setShowArchived(!showArchived); if (!showArchived) fetchArchivedDrills(); }}
                  className="border-gray-300 text-gray-600 text-xs h-7"
                >
                  {showArchived ? "Hide Archives" : "View Archives"}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleArchiveYear}
                  disabled={archiving || savedFireDrills.length === 0}
                  className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-7"
                >
                  {archiving ? "Archiving..." : "Archive Year & Start New"}
                </Button>
              </div>
            </div>

            {/* 60-day gap warning — checks ALL dates including future */}
            {(() => {
              const allDates = savedFireDrills
                .map(d => { try { return new Date(d.date + 'T12:00:00'); } catch { return null; } })
                .filter(Boolean)
                .sort((a, b) => a - b);
              const badGaps = [];
              for (let i = 0; i < allDates.length - 1; i++) {
                const gap = Math.round((allDates[i+1] - allDates[i]) / 86400000);
                if (gap > 60) {
                  badGaps.push({
                    from: allDates[i].toLocaleDateString('en-US', {month:'short',day:'numeric',year:'numeric'}),
                    to: allDates[i+1].toLocaleDateString('en-US', {month:'short',day:'numeric',year:'numeric'}),
                    days: gap
                  });
                }
              }
              if (badGaps.length === 0) return null;
              return (
                <div className="mb-3 bg-red-50 border border-red-300 rounded-lg px-4 py-3">
                  <p className="text-red-700 font-semibold text-sm mb-1">⚠️ Gap exceeds 60 days — date(s) need correction:</p>
                  {badGaps.map((g, i) => (
                    <p key={i} className="text-red-600 text-xs">
                      • {g.from} → {g.to} = <strong>{g.days} days</strong> (must be 60 or less)
                    </p>
                  ))}
                </div>
              );
            })()}

            {savedFireDrills.length === 0 ? (
              <p className="text-gray-400 italic text-sm text-center py-4">No drills logged yet — click Add Entry above to begin.</p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {savedFireDrills.map((drill) => (
                  <div key={drill.id} className="bg-white rounded-lg border border-purple-200">
                    {editingDrillId === drill.id ? (
                      <div className="p-3 space-y-2">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          <div>
                            <Label className="text-xs text-gray-600">Date</Label>
                            <Input type="date" value={editDrillValues.date} onChange={e => setEditDrillValues(p => ({...p, date: e.target.value}))} className="h-8 text-xs mt-1" />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-600">Start Time</Label>
                            <div className="flex gap-1 mt-1">
                              <select value={editDrillValues.timeHour} onChange={e => setEditDrillValues(p => ({...p, timeHour: e.target.value}))} className="border rounded px-1 py-1 text-xs w-12">
                                {Array.from({length:12},(_,i)=>i+1).map(h => <option key={h} value={String(h)}>{h}</option>)}
                              </select>
                              <select value={editDrillValues.timeMinute} onChange={e => setEditDrillValues(p => ({...p, timeMinute: e.target.value}))} className="border rounded px-1 py-1 text-xs w-14">
                                {['00','05','10','15','20','25','30','35','40','45','50','55'].map(m => <option key={m} value={m}>{m}</option>)}
                              </select>
                              <select value={editDrillValues.timePeriod} onChange={e => setEditDrillValues(p => ({...p, timePeriod: e.target.value}))} className="border rounded px-1 py-1 text-xs w-14">
                                <option value="AM">AM</option><option value="PM">PM</option>
                              </select>
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-600">Location</Label>
                            <input list={`edit-loc-${drill.id}`} value={editDrillValues.hypotheticalLocation} onChange={e => setEditDrillValues(p => ({...p, hypotheticalLocation: e.target.value}))} className="border rounded px-2 py-1 text-xs h-8 w-full mt-1" placeholder="Location..." />
                            <datalist id={`edit-loc-${drill.id}`}>{LOCATION_PRESETS.map(l => <option key={l} value={l} />)}</datalist>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-600">Evacuation</Label>
                            <div className="flex gap-1 mt-1 items-center">
                              <Input type="number" value={editDrillValues.evacuationMinutes} onChange={e => setEditDrillValues(p => ({...p, evacuationMinutes: e.target.value}))} className="h-8 text-xs w-12" placeholder="m" />
                              <span className="text-xs">m</span>
                              <Input type="number" value={editDrillValues.evacuationSeconds} onChange={e => setEditDrillValues(p => ({...p, evacuationSeconds: e.target.value}))} className="h-8 text-xs w-12" placeholder="s" />
                              <span className="text-xs">s</span>
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-600">Children</Label>
                            <Input type="number" value={editDrillValues.numberOfChildren} onChange={e => setEditDrillValues(p => ({...p, numberOfChildren: e.target.value}))} className="h-8 text-xs mt-1 w-20" />
                          </div>
                          <div className="md:col-span-3">
                            <Label className="text-xs text-gray-600">Participating Staff</Label>
                            <Input value={editDrillValues.participatingStaff} onChange={e => setEditDrillValues(p => ({...p, participatingStaff: e.target.value}))} className="h-8 text-xs mt-1" placeholder="Staff names..." />
                          </div>
                        </div>
                        <div className="flex gap-2 pt-1">
                          <Button size="sm" onClick={() => handleSaveDrillEdit(drill.id)} className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs">
                            <Save className="w-3 h-3 mr-1" />Save
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingDrillId(null)} className="h-7 text-xs">Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="p-3 flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-purple-800">
                            {drill.date ? format(new Date(drill.date + 'T12:00:00'), 'MMM d, yyyy') : 'No date'} at {drill.time || 'No time'}
                          </p>
                          <p className="text-sm text-gray-600">
                            Location: {drill.hypotheticalLocation || 'N/A'} | Children: {drill.numberOfChildren || 'N/A'} | Evac: {drill.evacuationMinutes || '0'}m {drill.evacuationSeconds || '00'}s
                          </p>
                          <p className="text-xs text-gray-500">
                            Staff: {Array.isArray(drill.participatingStaff) ? drill.participatingStaff.join(', ') : (drill.participatingStaff || 'N/A')}
                          </p>
                        </div>
                        <div className="flex gap-1.5 flex-shrink-0 ml-2">
                          <Button onClick={() => startEditDrill(drill)} size="sm" variant="outline" className="border-blue-300 text-blue-600 h-7 w-7 p-0">
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                          <Button onClick={() => handleDeleteFireDrill(drill.id)} size="sm" variant="outline" className="border-red-300 text-red-700 h-7 w-7 p-0">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Archived drills */}
            {showArchived && (
              <div className="mt-4 border-t border-gray-200 pt-3">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Archived Years</p>
                {archivedDrills.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No archived drills yet.</p>
                ) : (
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {archivedDrills.map(d => (
                      <div key={d.id} className="text-xs text-gray-500 bg-gray-50 rounded px-3 py-1.5 border border-gray-200">
                        <span className="font-medium">{d.date ? format(new Date(d.date + 'T12:00:00'), 'MMM d, yyyy') : '?'}</span>
                        {' '}at {d.time || '?'} — {d.hypotheticalLocation || 'N/A'}
                        <span className="ml-2 text-gray-400">[{d.archiveYear || 'Archived'}]</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Fire Detection Section */}
          <div className="mt-8 pt-6 border-t-2 border-blue-200 print:border-black">
            {/* Compliance item row — matches compliance checklist UI */}
            {complianceItem && (
              <div className="print:hidden mb-4 rounded-xl border border-gray-200 bg-white shadow-sm px-4 py-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3 flex-wrap min-w-0">
                    {complianceItem.complianceStatus === 'complete'
                      ? <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                      : complianceItem.complianceStatus === 'expiring'
                      ? <AlertCircle className="w-6 h-6 text-yellow-500 flex-shrink-0" />
                      : <XCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                    }
                    <span className="font-semibold text-gray-800 text-sm truncate">{complianceItem.name}</span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full border">Facility Documents</span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full border">Weight: {complianceItem.weight || 8}</span>
                    {complianceItem.isEnabled === false && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200">Disabled</span>}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {/* Edit */}
                    <button onClick={() => { setEditItemForm({ name: complianceItem.name, weight: complianceItem.weight || 8, description: complianceItem.description || '' }); setShowEditItem(true); }}
                      className="p-1.5 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-300 text-gray-500 hover:text-blue-600 transition-all" title="Edit compliance item">
                      <Edit className="w-4 h-4" />
                    </button>
                    {/* Disable / Restore toggle */}
                    {complianceItem.isEnabled !== false ? (
                      <button onClick={handleToggleItem} className="p-1.5 rounded-lg border border-gray-200 hover:bg-amber-50 hover:border-amber-300 text-gray-500 hover:text-amber-600 transition-all" title="Temporarily disable">
                        <ToggleRight className="w-4 h-4" />
                      </button>
                    ) : (
                      <button onClick={handleToggleItem} className="p-1.5 rounded-lg border border-amber-200 bg-amber-50 text-amber-600 hover:bg-amber-100 transition-all" title="Restore item">
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    )}
                    {/* Delete */}
                    <button onClick={handleDeleteItem} className="p-1.5 rounded-lg border border-gray-200 hover:bg-red-50 hover:border-red-300 text-gray-500 hover:text-red-600 transition-all" title="Delete compliance item">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-1 ml-9 italic">Fire detection must be tested every 30 days — connected to the compliance checklist above</p>
              </div>
            )}

            {/* Edit Compliance Item Modal */}
            {showEditItem && (
              <div className="print:hidden fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-gray-800">Edit Compliance Item</h3>
                    <button onClick={() => setShowEditItem(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Name *</label>
                    <input value={editItemForm.name} onChange={e => setEditItemForm(p => ({ ...p, name: e.target.value }))} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Description</label>
                    <textarea value={editItemForm.description} onChange={e => setEditItemForm(p => ({ ...p, description: e.target.value }))} rows={3} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Weight (1–10)</label>
                    <input type="number" min={1} max={10} value={editItemForm.weight} onChange={e => setEditItemForm(p => ({ ...p, weight: parseInt(e.target.value) || 8 }))} className="mt-1 w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <button onClick={() => setShowEditItem(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                    <button onClick={handleSaveItemEdit} disabled={savingItem} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold">
                      {savingItem ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <h3 className="font-semibold text-lg text-blue-800 print:text-black mb-1">FIRE DETECTION DEVICES OR SYSTEMS</h3>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <input type="radio" id="manual_testing" name="testing_type" checked={testingType === 'manual'} onChange={() => setTestingType('manual')} className="mt-1 w-4 h-4 text-blue-600 print:hidden" />
                <div className="flex-1">
                  <Label htmlFor="manual_testing" className="text-blue-800 font-semibold cursor-pointer">MANUAL FIRE DETECTION TESTING DATE (Every 30 Days)</Label>
                  <p className="text-xs text-gray-500 mb-3">Enter date tested for each month and staff person initial the month after testing</p>
                  
                  {testingType === 'manual' && (
                    <div>
                      <div className="flex gap-2 mb-2">
                        <Button onClick={handleGenerateDates} size="sm" className="bg-blue-500 hover:bg-blue-600 text-white text-xs h-7 px-3">
                          Generate (30-day intervals)
                        </Button>
                        <Button onClick={handleClearAllDates} size="sm" variant="outline" className="border-blue-300 text-blue-700 text-xs h-7 px-3">
                          Clear All
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        {MONTH_NAMES.map((monthName, idx) => (
                          <div key={idx + 1} className="text-center">
                            <Label className="text-xs text-blue-700 font-semibold uppercase">{monthName}</Label>
                            <Input type="date" value={monthlyInspections[`month${idx + 1}`] || ""} onChange={(e) => updateMonthlyInspection(`month${idx + 1}`, e.target.value)} className="border-blue-200 text-xs h-7 mt-1 px-1" />
                            <Input
                              type="text"
                              maxLength={4}
                              value={monthlyInitials[`month${idx + 1}`] || ""}
                              onChange={e => setMonthlyInitials(p => ({...p, [`month${idx + 1}`]: e.target.value.toUpperCase()}))}
                              placeholder="Initial"
                              className="border-blue-200 text-xs h-6 mt-1 text-center font-bold tracking-widest px-1 w-16 mx-auto"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="text-center font-semibold text-blue-700 print:hidden">OR</div>

              <div className="flex items-start gap-3">
                <input type="radio" id="professional_testing" name="testing_type" checked={testingType === 'professional'} onChange={() => setTestingType('professional')} className="mt-1 w-4 h-4 text-blue-600 print:hidden" />
                <div className="flex-1">
                  <Label htmlFor="professional_testing" className="text-blue-800 font-semibold cursor-pointer">FIRE SAFETY PROFESSIONAL TESTING DATE (Annually)</Label>
                  <p className="text-xs text-gray-500">(Documentation must be attached)</p>
                  <Input type="date" value={professionalTestingDate} onChange={(e) => setProfessionalTestingDate(e.target.value)} className="border-blue-200 mt-2 max-w-xs" disabled={testingType !== 'professional'} />
                </div>
              </div>
            </div>

            {/* Purchase Date */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <Label className="text-blue-800 font-semibold">FIRE DETECTION DEVICE/SYSTEM PURCHASE DATE:</Label>
              <div className="flex flex-wrap items-start gap-3 mt-2">
                <Input type="date" value={devicePurchaseDate} onChange={(e) => setDevicePurchaseDate(e.target.value)} className="border-blue-200 max-w-xs" />

                {/* Optional receipt upload — not tied to compliance */}
                <div className="flex-1 min-w-[180px]">
                  {receiptMeta ? (
                    <div className="flex items-center gap-2 border border-blue-200 bg-white rounded-lg px-3 py-2 text-xs">
                      <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <span className="truncate flex-1 text-gray-700" title={receiptMeta.fileName}>{receiptMeta.fileName}</span>
                      <button onClick={handleReceiptDownload} data-testid="fire-receipt-download-btn" className="text-blue-600 hover:text-blue-800 print:hidden">
                        <Download className="w-4 h-4" />
                      </button>
                      <button onClick={handleReceiptDelete} data-testid="fire-receipt-delete-btn" className="text-red-500 hover:text-red-700 print:hidden">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div
                      onDrop={handleReceiptDrop}
                      onDragOver={handleReceiptDragOver}
                      onDragLeave={handleReceiptDragLeave}
                      onClick={() => receiptFileInputRef.current?.click()}
                      data-testid="fire-receipt-dropzone"
                      className={`print:hidden border-2 border-dashed rounded-lg px-3 py-2 text-center cursor-pointer transition-colors ${
                        receiptDragOver ? 'border-blue-500 bg-blue-100' : 'border-blue-300 bg-white'
                      }`}
                    >
                      <input
                        ref={receiptFileInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={handleReceiptFileChange}
                        disabled={uploadingReceipt}
                        className="hidden"
                        data-testid="fire-receipt-file-input"
                      />
                      <Upload className={`w-4 h-4 mx-auto mb-1 ${receiptDragOver ? 'text-blue-600' : 'text-blue-400'}`} />
                      <p className="text-xs text-blue-600 leading-tight">
                        {uploadingReceipt ? 'Uploading...' : (receiptDragOver ? 'Drop receipt here' : 'Upload receipt (optional)')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-between flex-wrap gap-2 print:hidden">
          <Button onClick={handleClearFireDrillLog} variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50">
            <Trash2 className="w-4 h-4 mr-2" />
            Clear Log
          </Button>
          <div className="flex gap-2">
            <Button onClick={handleSaveFireDetectionSettings} className="bg-green-600 hover:bg-green-700 text-white">
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </Button>
            <Button onClick={handleExportPDF} variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50">
              <FileText className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700 text-white">
              <Printer className="w-4 h-4 mr-2" />
              Print Log
            </Button>
          </div>
        </div>

      </CardContent>
    </Card>
  );
};

export default FireDrillLogTab;
